// RunAuditPanel — on-demand audit submission + progress.
//
// Three input modes share one panel (decision #18 — single modal with
// segmented control). Reuses className conventions from
// `tools/image-studio/ProcessingSection.jsx`:
//   - `studio-input-mode` + `studio-input-mode-tab` for the mode toggle
//   - `image-link-drop-zone` for the CSV drop zone
//   - Cloudscape `<ProgressBar>` for the run progress
//   - `cro-audit-log-tail` (this tool's class) for the streaming log box

import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Header,
  Button,
  Alert,
  SpaceBetween,
  ProgressBar,
  Select,
  FormField,
  Input,
  Textarea,
  ExpandableSection,
  Box,
} from '@cloudscape-design/components';

import { storeCROAuditApi } from './storeCROAuditApi.js';

const PDP_SAMPLE_OPTIONS = [
  { label: 'All bestsellers (recommended)', value: '0' },
  { label: 'Up to 3 PDPs (faster)', value: '3' },
  { label: 'Up to 10 PDPs', value: '10' },
];

// Hard caps that mirror the Lambda config + tracker.
const MAX_PASTE_STORES = 50;
const MAX_CSV_STORES = 1000;

export default function RunAuditPanel({ username, onComplete, onError, onViewRegistry }) {
  const [mode, setMode] = useState('single');

  // Single-store form.
  const [storeUrl, setStoreUrl] = useState('');
  const [shopId, setShopId] = useState('');

  // Paste-mode form.
  const [pasted, setPasted] = useState('');

  // CSV upload.
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null); // { count, rows: [{shop_id, store_url}] }
  const [csvDragOver, setCsvDragOver] = useState(false);
  const csvInputRef = useRef(null);

  // Settings.
  const [pdpSampleCount, setPdpSampleCount] = useState(PDP_SAMPLE_OPTIONS[0]);
  const [pagespeedKeys, setPagespeedKeys] = useState(
    () => loadPagespeedKeys()
  );

  // Submission + progress.
  const [submitting, setSubmitting] = useState(false);
  const [activeRun, setActiveRun] = useState(null); // { glue_run_id, total }
  const [progress, setProgress] = useState({ completed: 0, failed: 0, total: 0 });
  const [logTail, setLogTail] = useState('');
  const [statusLabel, setStatusLabel] = useState('');
  const [done, setDone] = useState(false);
  const [completionSummary, setCompletionSummary] = useState(null); // { stores: [...], duration }

  // Pre-fill from re-audit click on the registry table.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('cro-audit-reaudit');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.shop_id && parsed?.store_url) {
          setMode('single');
          setShopId(String(parsed.shop_id));
          setStoreUrl(String(parsed.store_url));
        }
        sessionStorage.removeItem('cro-audit-reaudit');
      }
    } catch {
      /* ignore */
    }
  }, []);

  // ── Validation ────────────────────────────────────────────────────────
  const parsedPaste = parsePasted(pasted);
  const canSubmit = (() => {
    if (submitting) return false;
    if (mode === 'single') {
      if (!storeUrl.trim() || !shopId.trim()) return false;
      if (!/^\d+$/.test(shopId.trim())) return false;
      return true;
    }
    if (mode === 'paste') {
      return parsedPaste.length > 0 && parsedPaste.length <= MAX_PASTE_STORES;
    }
    if (mode === 'csv') {
      return csvFile != null;
    }
    return false;
  })();

  // ── Submit ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    setDone(false);
    setProgress({ completed: 0, failed: 0, total: 0 });
    setLogTail('');
    setStatusLabel('Submitting...');

    try {
      const settings = {
        pdp_sample_count: parseInt(pdpSampleCount.value, 10),
        pagespeed_keys: pagespeedKeys.trim(),
      };

      let response;
      if (mode === 'single') {
        response = await storeCROAuditApi.submitSingleStore({
          shop_id: parseInt(shopId.trim(), 10),
          store_url: storeUrl.trim(),
          settings,
        });
      } else if (mode === 'paste') {
        response = await storeCROAuditApi.submitPasted({
          stores: parsedPaste,
          settings,
        });
      } else if (mode === 'csv') {
        response = await storeCROAuditApi.submitCsv(csvFile, settings);
      }

      setActiveRun({
        glue_run_id: response.glue_run_id,
        job_identifier: response.job_identifier,
        total: response.store_count || 1,
        startedAt: new Date().toISOString().replace(/:/g, '-').slice(0, 19) + 'Z',
      });
      setStatusLabel('Audit submitted, waiting on first results...');
    } catch (err) {
      onError?.(err.message || 'Audit submit failed');
      setSubmitting(false);
      setStatusLabel('');
    }
  };

  // ── Polling ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeRun) return undefined;
    let cancelled = false;
    const interval = setInterval(async () => {
      if (cancelled) return;
      try {
        const status = await storeCROAuditApi.getStatus(activeRun.glue_run_id);
        const logs = await storeCROAuditApi.getLogs(activeRun.job_identifier || activeRun.glue_run_id);

        if (cancelled) return;

        const p = status.progress || {};
        setProgress({
          completed: Number(p.completed || 0),
          failed: Number(p.failed || 0),
          total: Number(p.total || activeRun.total || 0),
        });
        setLogTail(typeof logs?.logs === 'string' ? formatLogLines(logs.logs) : '');
        setStatusLabel(`Status: ${status.status || 'running'}`);

        const terminal = ['SUCCEEDED', 'FAILED', 'STOPPED'].includes(status.status);
        if (terminal) {
          clearInterval(interval);
          if (cancelled) return;
          setSubmitting(false);
          setDone(true);
          setActiveRun(null);
          // Persist the (probably edited) PSI keys for next time.
          if (pagespeedKeys.trim()) savePagespeedKeys(pagespeedKeys.trim());
          // Fetch registry to build per-store summary
          try {
            const reg = await storeCROAuditApi.getRegistry();
            const auditedStores = (reg?.stores || []).filter(s =>
              s.latest_run_at && s.latest_run_at >= (activeRun.startedAt || '')
            );
            setCompletionSummary({
              stores: auditedStores.length > 0 ? auditedStores : reg?.stores || [],
              duration: status.execution_time || 0,
            });
          } catch { /* non-critical */ }
          onComplete?.();
        }
      } catch (err) {
        // Polling errors are non-fatal — just keep trying. Surface only if
        // we can't recover after a while; for v1 a single failure is fine.
      }
    }, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [activeRun]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── CSV drop handlers ─────────────────────────────────────────────────
  const handleCsvDrop = (e) => {
    e.preventDefault();
    setCsvDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) parseCsvForPreview(f);
  };
  const handleCsvZoneClick = () => csvInputRef.current?.click();
  const handleCsvInputChange = (e) => {
    const f = e.target.files?.[0];
    if (f) parseCsvForPreview(f);
    if (e.target) e.target.value = '';
  };

  const parseCsvForPreview = (file) => {
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result.replace(/^\ufeff/, ''); // strip BOM
        const lines = text.split(/\r?\n/).filter(Boolean);
        if (lines.length < 2) { setCsvPreview({ count: 0, rows: [] }); return; }
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
        const shopCol = headers.findIndex(h => ['shopid', 'shop_id', 'store_id'].includes(h));
        const urlCol = headers.findIndex(h => ['store_website_url', 'store_url', 'url', 'website', 'domain'].includes(h));
        if (shopCol < 0 || urlCol < 0) {
          setCsvPreview({ count: 0, rows: [], error: `Missing required columns. Found: ${headers.join(', ')}` });
          return;
        }
        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split(',').map(c => c.trim().replace(/^["']|["']$/g, ''));
          const shopId = parseInt(cols[shopCol], 10);
          const url = (cols[urlCol] || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
          if (shopId && url) rows.push({ shop_id: shopId, store_url: url });
        }
        setCsvPreview({ count: rows.length, rows: rows.slice(0, 5) });
      } catch {
        setCsvPreview({ count: 0, rows: [], error: 'Could not parse CSV' });
      }
    };
    reader.readAsText(file);
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Submit a single store, paste a small list, or upload a CSV. Stores get auto-enrolled into the weekly schedule on first audit."
        >
          ▶️ Run audit
        </Header>
      }
    >
      <SpaceBetween size="m">
        {/* Mode toggle — same shape as image-studio's input-mode tabs */}
        <div className="studio-input-mode">
          {[
            { id: 'single', icon: '🔹', label: 'Single store' },
            { id: 'paste', icon: '📋', label: 'Multiple (paste)' },
            { id: 'csv', icon: '📄', label: 'Upload CSV' },
          ].map((m) => (
            <button
              key={m.id}
              type="button"
              className={`studio-input-mode-tab ${mode === m.id ? 'active' : ''}`}
              onClick={() => setMode(m.id)}
              disabled={submitting}
            >
              {m.icon} {m.label}
            </button>
          ))}
        </div>

        {/* Inputs */}
        {mode === 'single' && (
          <SpaceBetween size="s">
            <FormField
              label="Store URL"
              description="e.g. navlik.com or smartbiz.in/goingnuts"
            >
              <Input
                value={storeUrl}
                onChange={({ detail }) => setStoreUrl(detail.value)}
                placeholder="navlik.com"
                disabled={submitting}
              />
            </FormField>
            <FormField label="Shop ID">
              <Input
                value={shopId}
                onChange={({ detail }) => setShopId(detail.value)}
                placeholder="6526"
                disabled={submitting}
                inputMode="numeric"
              />
            </FormField>
          </SpaceBetween>
        )}

        {mode === 'paste' && (
          <FormField
            label="Paste store list"
            description={`One store per line: shop_id, store_url. Up to ${MAX_PASTE_STORES} stores.`}
            errorText={
              parsedPaste.length > MAX_PASTE_STORES
                ? `Detected ${parsedPaste.length} stores — limit is ${MAX_PASTE_STORES}. Switch to CSV upload for larger lists.`
                : undefined
            }
          >
            <Textarea
              value={pasted}
              onChange={({ detail }) => setPasted(detail.value)}
              rows={8}
              placeholder={'6526, navlik.com\n18358, truke.in'}
              disabled={submitting}
            />
            {parsedPaste.length > 0 && parsedPaste.length <= MAX_PASTE_STORES && (
              <Box variant="awsui-key-label" margin={{ top: 'xxs' }}>
                Detected {parsedPaste.length} store
                {parsedPaste.length === 1 ? '' : 's'}. Estimated runtime ≈{' '}
                {estimateRuntime(parsedPaste.length, !!pagespeedKeys.trim())}.
              </Box>
            )}
          </FormField>
        )}

        {mode === 'csv' && (
          <div>
            <div
              role="button"
              tabIndex={0}
              className={['image-link-drop-zone', csvDragOver ? 'drag-over' : '', submitting ? 'disabled' : '']
                .filter(Boolean)
                .join(' ')}
              onClick={handleCsvZoneClick}
              onDrop={handleCsvDrop}
              onDragOver={(e) => {
                e.preventDefault();
                setCsvDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setCsvDragOver(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleCsvZoneClick();
              }}
              aria-label="Drop a CSV file here or click to select"
            >
              <p className="drop-zone-icon">📄</p>
              <p className="drop-zone-title">
                {csvFile
                  ? `✓ Selected: ${csvFile.name}`
                  : 'Drag & drop a CSV, or click to select'}
              </p>
              <p className="drop-zone-hint">
                Required columns: <code>shopid</code> + <code>store_website_url</code>.
                Up to {MAX_CSV_STORES} rows per run.
              </p>
            </div>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={handleCsvInputChange}
            />
            {/* Template download — below the drop zone */}
            <Box margin={{ top: 'xs' }}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  const csv = 'shopid,store_website_url\n6526,navlik.com\n33270,araynaa.com\n';
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'cro_audit_template.csv';
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                style={{ fontSize: '12px', color: '#0972d3' }}
              >
                📥 Download CSV template
              </a>
            </Box>
            {/* CSV preview after selection */}
            {csvPreview && (
              <Box margin={{ top: 's' }} padding="s" variant="code">
                {csvPreview.error ? (
                  <span style={{ color: '#d91515' }}>⚠️ {csvPreview.error}</span>
                ) : (
                  <>
                    <Box variant="strong">
                      ✓ {csvPreview.count} store{csvPreview.count === 1 ? '' : 's'} detected
                    </Box>
                    {csvPreview.rows.length > 0 && (
                      <Box margin={{ top: 'xxs' }} fontSize="body-s" color="text-body-secondary">
                        {csvPreview.rows.map((r, i) => (
                          <div key={i}>{r.shop_id} — {r.store_url}</div>
                        ))}
                        {csvPreview.count > 5 && <div>…and {csvPreview.count - 5} more</div>}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}
          </div>
        )}

        {/* Settings — collapsed by default */}
        <ExpandableSection headerText="Audit settings">
          <SpaceBetween size="s">
            <FormField
              label="PDP sample count"
              description="How many bestseller PDPs to fetch per store. All bestsellers gives the most accurate score; 3 is fastest for testing."
            >
              <Select
                selectedOption={pdpSampleCount}
                onChange={({ detail }) => setPdpSampleCount(detail.selectedOption)}
                options={PDP_SAMPLE_OPTIONS}
                disabled={submitting}
              />
            </FormField>
            <FormField
              label="PageSpeed Insights API keys"
              description="Optional. Comma-separated. Without keys, LCP scores are skipped (max_score=0, no penalty). Keys are stored in your browser's localStorage and never persisted to S3."
            >
              <Input
                type="password"
                value={pagespeedKeys}
                onChange={({ detail }) => setPagespeedKeys(detail.value)}
                placeholder="key1,key2,key3"
                disabled={submitting}
              />
            </FormField>
          </SpaceBetween>
        </ExpandableSection>

        {/* Submit */}
        <Box float="right">
          <Button
            variant="primary"
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            Start audit
          </Button>
        </Box>

        {/* Progress + Summary */}
        {(activeRun || done) && (
          <SpaceBetween size="s">
            <ProgressBar
              value={
                progress.total > 0
                  ? Math.round(((progress.completed + progress.failed) / progress.total) * 100)
                  : 0
              }
              additionalInfo={
                progress.total > 0
                  ? `${progress.completed + progress.failed} of ${progress.total} done${progress.failed ? ` (${progress.failed} failed)` : ''}`
                  : 'Waiting on first results...'
              }
              description={statusLabel}
            />
            {done && (
              <Container>
                <SpaceBetween size="s">
                  <Box variant="h3">
                    ✅ Audit completed
                  </Box>
                  <Box variant="p" color="text-body-secondary">
                    {progress.completed + progress.failed} store{progress.completed + progress.failed === 1 ? '' : 's'} audited
                    {progress.completed > 0 && ` · ${progress.completed} succeeded`}
                    {progress.failed > 0 && ` · ${progress.failed} failed`}
                    {completionSummary?.duration > 0 && ` · Duration: ~${completionSummary.duration}s`}
                  </Box>
                  {completionSummary?.stores?.length > 0 && (
                    <Box padding={{ top: 'xs', bottom: 'xs' }}>
                      <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                        <tbody>
                          {completionSummary.stores.slice(0, 10).map((s) => (
                            <tr key={s.shop_id} style={{ borderBottom: '1px solid #e9ecef' }}>
                              <td style={{ padding: '6px 8px', fontWeight: 500 }}>{s.store_name || s.store_url}</td>
                              <td style={{ padding: '6px 8px', color: '#64748b' }}>{s.store_url}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                                {s.latest_status === 'failed' ? (
                                  <span style={{ color: '#dc2626' }}>✗ Failed</span>
                                ) : (
                                  <span style={{ color: s.latest_score_percentage >= 80 ? '#16a34a' : s.latest_score_percentage >= 50 ? '#ca8a04' : '#dc2626' }}>
                                    {Number(s.latest_score || 0).toFixed(1)} / {Number(s.latest_max_score || 0).toFixed(1)} ({Number(s.latest_score_percentage || 0).toFixed(0)}%)
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {completionSummary.stores.length > 10 && (
                        <Box color="text-body-secondary" fontSize="body-s" margin={{ top: 'xxs' }}>
                          …and {completionSummary.stores.length - 10} more
                        </Box>
                      )}
                    </Box>
                  )}
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="primary" onClick={() => onViewRegistry?.()}>
                      View in Registry
                    </Button>
                    <Button
                      onClick={() => {
                        setDone(false);
                        setActiveRun(null);
                        setCompletionSummary(null);
                        setProgress({ completed: 0, failed: 0, total: 0 });
                        setLogTail('');
                        setStatusLabel('');
                        setCsvFile(null);
                        setCsvPreview(null);
                      }}
                    >
                      Run another audit
                    </Button>
                  </SpaceBetween>
                </SpaceBetween>
              </Container>
            )}
            {logTail && (
              <ExpandableSection headerText="Job logs" defaultExpanded={!done}>
                <pre className="cro-audit-log-tail">{logTail}</pre>
              </ExpandableSection>
            )}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Container>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function formatLogLines(raw) {
  // Parse structured JSON log lines and extract just the human-readable message.
  return raw
    .split('\n')
    .filter(Boolean)
    .map((line) => {
      try {
        const parsed = JSON.parse(line);
        return parsed.message || line;
      } catch {
        return line;
      }
    })
    .slice(-50) // last 50 lines
    .join('\n');
}

function parsePasted(text) {
  if (!text || !text.trim()) return [];
  const out = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const parts = line.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length < 2) continue;
    const shopId = parseInt(parts[0], 10);
    if (!Number.isFinite(shopId)) continue;
    const storeUrl = parts.slice(1).join(',').trim();
    if (!storeUrl) continue;
    out.push({ shop_id: shopId, store_url: storeUrl });
  }
  return out;
}

function estimateRuntime(storeCount, hasPSI) {
  // Rough: PSI-on ≈ 3 min/store; PSI-off ≈ 30 sec/store; concurrency 8 in
  // the Glue worker.
  const perStoreMin = hasPSI ? 3 : 0.5;
  const totalMin = Math.ceil((storeCount * perStoreMin) / 8);
  if (totalMin < 1) return '< 1 min';
  if (totalMin < 60) return `${totalMin} min`;
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
}

function loadPagespeedKeys() {
  try {
    return localStorage.getItem('cro-audit-pagespeed-keys') || '';
  } catch {
    return '';
  }
}

function savePagespeedKeys(value) {
  try {
    localStorage.setItem('cro-audit-pagespeed-keys', value);
  } catch {
    /* ignore */
  }
}
